import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  BarChart3,
  Building2,
  CalendarRange,
  Clock,
  DatabaseZap,
  FileText,
  ListChecks,
  LogOut,
  Menu,
  Palette,
  ScrollText,
  Settings,
  Users,
  UsersRound,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { todayIn } from '@tt/shared';
import { NotificationBell } from '@/components/notification-bell';
import { MustChangePasswordPage } from '@/features/auth/must-change-password-page';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useMe } from '@/lib/queries';
import { cn } from '@/lib/utils';

export function AppShell() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const [open, setOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const today = todayIn(me?.timeZone ?? 'UTC');

  // An administrator reset this password: the application is replaced by the change-password
  // screen, with no navigation to click past. The server refuses everything else anyway.
  const mustChangePassword = me?.mustChangePassword === true;

  const nav = [
    {
      to: '/time/week/$date',
      params: { date: today },
      label: t('nav.time'),
      icon: Clock,
      active: pathname.startsWith('/time'),
    },
    {
      to: '/dashboard',
      params: {},
      label: t('nav.dashboard'),
      icon: BarChart3,
      active: pathname.startsWith('/dashboard'),
    },
    // Admin entries are hidden for regular users; the server still enforces the real rule.
    ...(me?.role === 'ADMIN'
      ? ([
          {
            to: '/admin/dashboard',
            params: {},
            label: t('admin.nav.dashboard'),
            icon: Building2,
            active: pathname.startsWith('/admin/dashboard'),
          },
          {
            to: '/admin/users',
            params: {},
            label: t('admin.nav.users'),
            icon: Users,
            active: pathname.startsWith('/admin/users'),
          },
          {
            to: '/admin/groups',
            params: {},
            label: t('admin.nav.groups'),
            icon: UsersRound,
            active: pathname.startsWith('/admin/groups'),
          },
          {
            to: '/admin/projects',
            params: {},
            label: t('admin.nav.projects'),
            icon: Palette,
            active: pathname.startsWith('/admin/projects'),
          },
          {
            to: '/admin/activities',
            params: {},
            label: t('admin.nav.activities'),
            icon: ListChecks,
            active: pathname.startsWith('/admin/activities'),
          },
          {
            to: '/admin/timesheets',
            params: {},
            label: t('admin.nav.timesheets'),
            icon: CalendarRange,
            active: pathname.startsWith('/admin/timesheets'),
          },
          {
            to: '/admin/reports',
            params: {},
            label: t('admin.nav.reports'),
            icon: FileText,
            active: pathname.startsWith('/admin/reports'),
          },
          {
            to: '/admin/audit-log',
            params: {},
            label: t('admin.nav.audit'),
            icon: ScrollText,
            active: pathname.startsWith('/admin/audit-log'),
          },
          {
            to: '/admin/settings',
            params: {},
            label: t('admin.nav.settings'),
            icon: Building2,
            active: pathname.startsWith('/admin/settings'),
          },
          {
            to: '/admin/maintenance',
            params: {},
            label: t('admin.nav.maintenance'),
            icon: DatabaseZap,
            active: pathname.startsWith('/admin/maintenance'),
          },
        ] as const)
      : []),
    {
      to: '/settings/profile',
      params: {},
      label: t('nav.settings'),
      icon: Settings,
      active: pathname.startsWith('/settings'),
    },
  ] as const;

  if (mustChangePassword) return <MustChangePasswordPage />;

  return (
    <div className="min-h-screen bg-muted/30 md:flex">
      <a
        href="#main"
        className="sr-only-focusable fixed left-2 top-2 z-[60] rounded bg-background px-3 py-2 text-sm shadow"
      >
        {t('app.skipToContent')}
      </a>

      {/* Sidebar (desktop) / drawer (mobile) */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 w-60 border-r bg-background transition-transform md:static md:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label={t('nav.main')}
      >
        <div className="flex h-14 items-center justify-between px-4">
          <Link to="/" className="flex items-center gap-2 font-semibold">
            <Clock className="h-5 w-5 text-primary" aria-hidden="true" />
            {t('app.name')}
          </Link>
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setOpen(false)}
            aria-label={t('app.close')}
          >
            <X />
          </Button>
        </div>
        <nav className="space-y-1 px-2 py-2">
          {nav.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              params={item.params}
              onClick={() => setOpen(false)}
              aria-current={item.active ? 'page' : undefined}
              className={cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium hover:bg-accent',
                item.active ? 'bg-accent text-accent-foreground' : 'text-muted-foreground',
              )}
            >
              <item.icon className="h-4 w-4" aria-hidden="true" />
              {item.label}
            </Link>
          ))}
        </nav>
      </aside>
      {open ? (
        <button
          type="button"
          className="fixed inset-0 z-30 bg-black/30 md:hidden"
          aria-label={t('app.close')}
          onClick={() => setOpen(false)}
        />
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-14 items-center gap-2 border-b bg-background px-3 sm:px-4">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setOpen(true)}
            aria-label={t('app.openMenu')}
          >
            <Menu />
          </Button>
          {/* Keeps the bell and the user menu to the right now that nothing sits here. */}
          <div className="min-w-0 flex-1" />
          <NotificationBell />
          <UserMenu />
        </header>
        <main id="main" tabIndex={-1} className="flex-1 p-3 sm:p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function UserMenu() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const initials = me ? `${me.firstName[0] ?? ''}${me.lastName[0] ?? ''}`.toUpperCase() : '';

  const signOut = async () => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } finally {
      qc.clear();
      await navigate({ to: '/login', search: {} });
    }
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground"
          aria-label={me ? `${me.firstName} ${me.lastName}` : t('app.openMenu')}
        >
          {initials}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-50 min-w-48 rounded-md border bg-popover p-1 text-sm shadow-md"
        >
          <div className="px-2 py-1.5">
            <div className="font-medium">{me ? `${me.firstName} ${me.lastName}` : ''}</div>
            <div className="truncate text-xs text-muted-foreground">{me?.email}</div>
          </div>
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.Item asChild>
            <Link
              to="/settings/profile"
              className="flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 outline-none data-[highlighted]:bg-accent"
            >
              <Settings className="h-4 w-4" aria-hidden="true" /> {t('nav.settings')}
            </Link>
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onSelect={() => void signOut()}
            className="flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 outline-none data-[highlighted]:bg-accent"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" /> {t('app.signOut')}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
